import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import PageHeader from "./PageHeader";
import { countryName } from "./countries";
import {
  API_BASE_URL,
  ChartTip,
  DeltaText,
  PeriodSwitch,
  STAGE_LABELS,
  TYPE_META,
  dayLabel,
  delta,
  num,
  periodParams,
  plural,
  rub,
  secs,
  whenLabel,
} from "./analyticsShared";
import { docTypeMeta } from "./docTypes";
import "./analytics.css";

const axisTick = { fontSize: 12, fill: "#6B6D75" };
const PAID = "#D0643B";
const FREE = "#5B7FB8";

const initials = (name) =>
  (name || "?")
    .replace(/[«»"']/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const Dialog = ({ title, onClose, children }) => {
  const closeRef = useRef(null);
  // onClose меняется на каждом рендере родителя: держим его в ref, иначе эффект
  // перезапускался бы при вводе и уводил фокус из поля на кнопку закрытия
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const opener = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onCloseRef.current();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, []);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content an-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button ref={closeRef} type="button" className="modal-close-btn" aria-label="Закрыть" onClick={onClose}>
            <X size={22} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const docLabel = (type) => {
  const meta = docTypeMeta(type);
  return meta.fullLabel || meta.label;
};

const DOC_FILTERS = [
  { key: "all", label: "Все" },
  { key: "paid", label: "Платные" },
  { key: "errors", label: "С ошибкой", tone: "warn" },
];

const AdminCompanyAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [period, setPeriod] = useState(() => {
    if (search.get("start") && search.get("end"))
      return { custom: { start: search.get("start"), end: search.get("end") } };
    return { days: Number(search.get("days")) || 30 };
  });
  const [docFilter, setDocFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    return axios
      .get(`${API_BASE_URL}/api/admin/analytics/companies/${id}`, {
        params: { ...periodParams(period), docs: docFilter, page },
      })
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((e) =>
        setError(e.response?.status === 404 ? "Компания не найдена" : "Не удалось загрузить данные компании"),
      );
  }, [id, period, docFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data)
    return (
      <>
        <PageHeader title="Компания" onBack={() => navigate("/admin")} backLabel="Назад к аналитике" />
        <div className="px-empty" role={error ? "alert" : "status"}>
          {error || "Загружаем…"}
        </div>
      </>
    );

  const { company: c, summary: s, previous: p } = data;
  const series = data.daily.map((d) => {
    const total = ["passport", "drivers_license", "civil", "contract", "other"].reduce((n, t) => n + (d[t] || 0), 0);
    return { ...d, paidN: d.paid, freeN: total - d.paid, total };
  });
  const maxMix = Math.max(1, ...data.by_type.map((t) => t.docs));
  const docs = data.documents;

  const runway =
    c.runway_days == null
      ? c.free_left > 0
        ? `Бесплатных распознаваний: ${c.free_left}`
        : "Платных документов за период не было"
      : c.runway_days < 1
        ? "Баланса не хватает — нужно пополнить"
        : `При текущем расходе хватит на ≈ ${Math.round(c.runway_days)} ${plural(Math.round(c.runway_days), "день", "дня", "дней")}`;

  const submit = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      setDialog(null);
      setNotice(okText);
      await load();
    } catch (e) {
      setNotice(e.response?.data?.error || "Не получилось сохранить");
    } finally {
      setBusy(false);
    }
  };

  const toggleAnalytics = () =>
    submit(
      () => axios.post(`${API_BASE_URL}/api/admin/companies/${c.id}/analytics`, { exclude: !c.excluded }),
      c.excluded ? "Компания снова учитывается в аналитике" : "Компания исключена из общей аналитики",
    );

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/documents/${doc.record_id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.title || `document_${doc.record_id}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setNotice("Файл документа недоступен");
    }
  };

  return (
    <div className="an">
      <PageHeader
        onBack={() => navigate("/admin")}
        backLabel="Назад к аналитике"
        leading={
          <span className="res-type-tile tone-sky" aria-hidden="true" style={{ fontWeight: 800, fontSize: 18 }}>
            {initials(c.name)}
          </span>
        }
        title={c.name}
        subtitle={[
          `ИНН ${c.inn}`,
          c.client_since && `клиент с ${new Date(c.client_since).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })}`,
          `${data.staff.length} ${plural(data.staff.length, "сотрудник", "сотрудника", "сотрудников")}`,
          `тариф ${rub(c.price)} за страницу`,
        ]
          .filter(Boolean)
          .join(" · ")}
      />

      <div className="an-actions">
        <button
          type="button"
          role="switch"
          aria-checked={!c.excluded}
          className="an-switch is-header"
          onClick={toggleAnalytics}
          disabled={busy}
        >
          <span className="an-switch-track" aria-hidden="true">
            <span />
          </span>
          Учитывать в аналитике
        </button>
        <button
          type="button"
          className="px-btn px-btn-outline"
          onClick={() => {
            setForm({ price: String(c.price) });
            setDialog("price");
          }}
        >
          Изменить тариф
        </button>
        <button
          type="button"
          className="px-btn px-btn-ink"
          onClick={() => {
            setForm({ user: String(data.staff[0]?.id || ""), amount: "" });
            setDialog("balance");
          }}
          disabled={data.staff.length === 0}
        >
          Пополнить баланс
        </button>
      </div>

      {notice && (
        <p className="an-notice" role="status">
          {notice}
          <button type="button" className="px-icon-btn is-small" aria-label="Скрыть" onClick={() => setNotice(null)}>
            <X size={16} aria-hidden="true" />
          </button>
        </p>
      )}

      <div className="an-toolbar is-plain">
        <PeriodSwitch
          period={period}
          onChange={(np) => {
            setPeriod(np);
            setPage(1);
          }}
        />
        <span className="an-sub" style={{ margin: 0 }}>
          {dayLabel(data.period.start)} — {dayLabel(data.period.end)} · сравнение с прошлым таким же периодом
        </span>
      </div>

      <section className="an-kpis" aria-label="Показатели компании">
        <div className="an-kpi">
          <span className="an-kpi-label">Выручка</span>
          <span className="an-kpi-value">{rub(s.revenue)}</span>
          <span className="an-kpi-sub">{num(s.paid)} {plural(s.paid, "платный документ", "платных документа", "платных документов")}</span>
          <DeltaText d={delta(s.revenue, p.revenue)} />
        </div>
        <div className="an-kpi">
          <span className="an-kpi-label">Себестоимость AI</span>
          <span className="an-kpi-value">{rub(s.ai_cost)}</span>
          <span className="an-kpi-sub">{rub(s.ai_per_doc, 2)} на документ</span>
          <DeltaText d={delta(s.ai_cost, p.ai_cost, { higherIsBetter: false })} />
        </div>
        <div className="an-kpi">
          <span className="an-kpi-label">Прибыль</span>
          <span className="an-kpi-value">{rub(s.profit)}</span>
          <span className="an-kpi-sub">{s.margin == null ? "Выручки не было" : `Маржа ${Math.round(s.margin)}%`}</span>
          <DeltaText d={s.margin != null && p.margin != null ? delta(s.margin, p.margin, { pp: true }) : delta(s.profit, p.profit)} />
        </div>
        <div className="an-kpi">
          <span className="an-kpi-label">Документы</span>
          <span className="an-kpi-value">{num(s.docs)}</span>
          <span className="an-kpi-sub">
            {num(s.paid)} платных · {num(s.free)} бесплатных
            {data.speed.current.median_ms != null && ` · обычно ${secs(data.speed.current.median_ms)}`}
          </span>
          <DeltaText d={delta(s.docs, p.docs)} />
        </div>
        <div className="an-kpi is-sky">
          <span className="an-kpi-label">Баланс</span>
          <span className="an-kpi-value">{rub(c.balance)}</span>
          <span className="an-kpi-sub">{runway}</span>
        </div>
      </section>

      <div className="an-row">
        <section className="an-card an-grow-2" aria-labelledby="co-days">
          <div className="an-card-head">
            <div>
              <h2 id="co-days" className="an-title">Документы по дням</h2>
              <p className="an-sub">Платные и бесплатные</p>
            </div>
            <span className="an-legend">
              <span><i style={{ background: FREE }} />Бесплатные</span>
              <span><i style={{ background: PAID }} />Платные</span>
            </span>
          </div>
          <div className="an-chart is-short" role="img" aria-label={`Документы компании по дням, всего ${num(s.docs)}`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="#F0F1F3" />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={axisTick} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(22,23,27,0.04)" }}
                  content={<ChartTip rows={(d) => [["Бесплатные", num(d.freeN)], ["Платные", num(d.paidN)], ["Выручка", rub(d.revenue)]]} />}
                />
                <Bar isAnimationActive={false} dataKey="freeN" stackId="d" fill={FREE} stroke="#FFFFFF" strokeWidth={1} maxBarSize={18} />
                <Bar isAnimationActive={false} dataKey="paidN" stackId="d" fill={PAID} stroke="#FFFFFF" strokeWidth={1} maxBarSize={18} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="an-card an-grow-1" aria-labelledby="co-mix">
          <h2 id="co-mix" className="an-title">Что переводят</h2>
          <p className="an-sub">Документы и себестоимость по типам</p>
          {data.by_type.length === 0 && <p className="an-muted">Документов за период нет</p>}
          {data.by_type.map((t) => {
            const meta = TYPE_META[t.type] || TYPE_META.other;
            return (
              <div key={t.type} className="an-mix">
                <div>
                  <strong>{meta.label}</strong>
                  <span className="an-muted">
                    {num(t.docs)} · AI {rub(t.ai_cost)}
                  </span>
                </div>
                <span className="an-bar">
                  <span style={{ width: `${(t.docs / maxMix) * 100}%`, background: meta.color }} />
                </span>
              </div>
            );
          })}
        </section>
      </div>

      <section className="an-card" aria-labelledby="co-staff">
        <h2 id="co-staff" className="an-title">Сотрудники</h2>
        <div className="px-table-wrap">
          <table className="an-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th scope="col">Сотрудник</th>
                <th scope="col" className="num">Документов</th>
                <th scope="col" className="num">Выручка</th>
                <th scope="col" className="num">Баланс</th>
                <th scope="col">Последний документ</th>
              </tr>
            </thead>
            <tbody>
              {data.staff.map((u) => (
                <tr key={u.id}>
                  <td><strong>{u.email}</strong></td>
                  <td className="num">{num(u.docs)}</td>
                  <td className="num">{rub(u.revenue)}</td>
                  <td className="num">{rub(u.balance)}</td>
                  <td className="an-muted">{whenLabel(u.last_doc_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="an-card" aria-labelledby="co-docs">
        <div className="an-card-head">
          <div>
            <h2 id="co-docs" className="an-title">Документы</h2>
            <p className="an-sub">Сколько принёс и сколько стоил каждый документ</p>
          </div>
          <div className="an-pills" role="radiogroup" aria-label="Показать документы">
            {DOC_FILTERS.map((f) => {
              const n = f.key === "all" ? s.docs : f.key === "paid" ? s.paid : data.errors.total;
              const active = docFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`an-pill${active ? " is-active" : ""}${f.tone && n > 0 && !active ? " is-warn" : ""}`}
                  onClick={() => {
                    setDocFilter(f.key);
                    setPage(1);
                  }}
                >
                  {f.label} · {num(n)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-table-wrap">
          {docFilter === "errors" ? (
            <table className="an-table" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Документ</th>
                  <th scope="col">Сотрудник</th>
                  <th scope="col">Этап</th>
                  <th scope="col">Причина</th>
                </tr>
              </thead>
              <tbody>
                {docs.items.map((d, i) => (
                  <tr key={`${d.created_at}-${i}`}>
                    <td className="an-muted an-nowrap">{whenLabel(d.created_at)}</td>
                    <td>
                      <strong>{docLabel(d.doc_type)}</strong>
                      <span className="an-cell-sub">{d.title || "—"}</span>
                    </td>
                    <td className="an-muted">{d.user ? d.user.split("@")[0] : "—"}</td>
                    <td>{STAGE_LABELS[d.stage] || d.stage || "—"}</td>
                    <td className="an-bad">{d.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="an-table" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Документ</th>
                  <th scope="col">Сотрудник</th>
                  <th scope="col" className="num">Выручка</th>
                  <th scope="col" className="num">AI</th>
                  <th scope="col" className="num">Итог</th>
                  <th scope="col" className="num">Время</th>
                  <th scope="col"><span className="sr-only">Скачать</span></th>
                </tr>
              </thead>
              <tbody>
                {docs.items.map((d, i) => (
                  <tr key={`${d.created_at}-${i}`}>
                    <td className="an-muted an-nowrap">{whenLabel(d.created_at)}</td>
                    <td>
                      <strong>{docLabel(d.doc_type)}</strong>
                      <span className="an-cell-sub">
                        {[
                          d.title,
                          d.country && countryName(d.country),
                          d.source_language && d.source_language !== "auto" && `${d.source_language.toUpperCase()} → ${(d.target_language || "ru").toUpperCase()}`,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    </td>
                    <td className="an-muted">{d.user ? d.user.split("@")[0] : "—"}</td>
                    <td className="num">{rub(d.revenue)}</td>
                    <td className="num an-muted">{rub(d.ai_cost, 2)}</td>
                    <td className={`num ${d.net >= 0 ? "an-good" : "an-bad"}`}>
                      {d.net >= 0 ? "+" : "−"}
                      {rub(Math.abs(d.net), 2)}
                    </td>
                    <td className="num an-muted an-nowrap">{secs(d.duration_ms)}</td>
                    <td className="num">
                      {d.has_file && (
                        <button
                          type="button"
                          className="px-icon-btn is-small"
                          aria-label={`Скачать: ${docLabel(d.doc_type)}${d.title ? `, ${d.title}` : ""}`}
                          onClick={() => download(d)}
                        >
                          <Download size={16} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {docs.items.length === 0 && <p className="an-muted" style={{ padding: "16px 0" }}>Ничего нет за период</p>}
        </div>
        {docs.pages > 1 && (
          <nav className="hist-pages an-pages" aria-label="Страницы документов">
            <button type="button" className="px-icon-btn is-small" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <span className="hist-pages-info">{page} из {docs.pages}</span>
            <button type="button" className="px-icon-btn is-small" aria-label="Следующая страница" disabled={page >= docs.pages} onClick={() => setPage(page + 1)}>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </nav>
        )}
      </section>

      {dialog === "price" && (
        <Dialog title="Тариф компании" onClose={() => setDialog(null)}>
          <form
            className="an-form"
            onSubmit={(e) => {
              e.preventDefault();
              submit(
                () => axios.post(`${API_BASE_URL}/api/admin/companies/${c.id}/price`, { price: Number(form.price) }),
                "Тариф обновлён",
              );
            }}
          >
            <label className="rec-field">
              <span className="rec-label">Цена за страницу, ₽</span>
              <input
                className="rec-select"
                type="number"
                min="0"
                step="1"
                required
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </label>
            <div className="mrg-actions">
              <button type="button" className="px-btn px-btn-outline" onClick={() => setDialog(null)}>Отмена</button>
              <button type="submit" className="px-btn px-btn-ink" disabled={busy}>{busy ? "Сохраняем…" : "Сохранить"}</button>
            </div>
          </form>
        </Dialog>
      )}

      {dialog === "balance" && (
        <Dialog title="Пополнить баланс" onClose={() => setDialog(null)}>
          <form
            className="an-form"
            onSubmit={(e) => {
              e.preventDefault();
              const user = data.staff.find((u) => String(u.id) === form.user);
              submit(
                () => axios.post(`${API_BASE_URL}/api/admin/users/${form.user}/balance`, { amount: Number(form.amount) }),
                `Баланс ${user?.email || ""} изменён на ${rub(Number(form.amount))}`,
              );
            }}
          >
            <p className="an-sub" style={{ margin: 0 }}>Баланс хранится у каждого сотрудника отдельно.</p>
            <label className="rec-field">
              <span className="rec-label">Сотрудник</span>
              <select className="rec-select" value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })}>
                {data.staff.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email} — {rub(u.balance)}
                  </option>
                ))}
              </select>
            </label>
            <label className="rec-field">
              <span className="rec-label">Сумма, ₽ (минус — списать)</span>
              <input
                className="rec-select"
                type="number"
                step="1"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </label>
            <div className="mrg-actions">
              <button type="button" className="px-btn px-btn-outline" onClick={() => setDialog(null)}>Отмена</button>
              <button type="submit" className="px-btn px-btn-ink" disabled={busy || !form.amount || Number(form.amount) === 0}>
                {busy ? "Сохраняем…" : "Изменить баланс"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
};

export default AdminCompanyAnalytics;
